import { splitAtPageBreaks } from '@features/page-split';
import { anchorTextOf } from '@root/helpers/comments/anchor-text';

import type { ThreadMarkInput } from './inject-thread-marks';

/** Where a page segment's (trimmed) content starts inside the whole block's anchor text. */
export const getSegmentAnchorOffset = (fullContent: string, segmentIndex: number): number => {
  const parts = splitAtPageBreaks(fullContent);
  // paginate() keeps an unsplit block's content raw, so nothing was trimmed.
  if (parts.length === 1) {
    return 0;
  }
  const before = parts.slice(0, segmentIndex).join('');
  const part = parts[segmentIndex] ?? '';
  // paginate() trims each part; the trimmed leading whitespace is raw text, so it maps 1:1 onto anchor text.
  return anchorTextOf(before).length + (part.length - part.trimStart().length);
};

/** Block-level anchors expressed in one segment's coordinates; anchors crossing the break are clipped. */
export const projectAnchorsOntoSegment = (
  anchors: ThreadMarkInput[],
  offset: number,
  segmentLength: number,
): ThreadMarkInput[] =>
  anchors.flatMap((anchor) => {
    const start = Math.max(anchor.start - offset, 0);
    const end = Math.min(anchor.end - offset, segmentLength);
    if (start >= end) {
      return [];
    }
    const textStart = start - (anchor.start - offset);
    return [{ ...anchor, start, end, text: anchor.text.slice(textStart, textStart + (end - start)) }];
  });
